import { useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Mail, User, Home, ShieldAlert, SparkSolid } from "iconoir-react";
import { requestMagicLink } from "@/lib/api";
import { toast } from "sonner";

function formatError(detail) {
    if (!detail) return "Something went wrong";
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
    return String(detail);
}

export default function Signup() {
    const nav = useNavigate();
    const [params] = useSearchParams();
    const presetCode = (params.get("code") || "").toUpperCase().trim();

    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [householdName, setHouseholdName] = useState("");
    const [busy, setBusy] = useState(false);
    const [sent, setSent] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (!name.trim() || !email.trim()) {
            toast.error("Name and email please");
            return;
        }
        setBusy(true);
        try {
            // Persist invite intent so it survives any redirect chain
            if (presetCode) sessionStorage.setItem("milo_pending_code", presetCode);
            await requestMagicLink({
                email: email.trim(),
                name: name.trim(),
                invite_code: presetCode || undefined,
                household_name: !presetCode && householdName.trim() ? householdName.trim() : undefined,
            });
            setSent(true);
        } catch (err) {
            toast.error(formatError(err?.response?.data?.detail) || "Couldn't send link");
        } finally {
            setBusy(false);
        }
    };

    if (sent) {
        return (
            <div className="auth-bg min-h-screen w-full flex items-center justify-center p-5">
                <div className="auth-sun" />
                <div className="auth-heart" />
                <motion.div
                    initial={{ opacity: 0, y: 14 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="relative z-10 w-full max-w-md"
                >
                    <div className="flex flex-col items-center text-center mb-6">
                        <img src="/milo-logo.png" alt="Milo" className="h-14 w-auto object-contain mb-6" />
                    </div>
                    <div className="bg-white/80 backdrop-blur-xl rounded-[28px] p-6 sm:p-7 shadow-[0_2px_4px_rgba(13,42,27,0.04),0_24px_48px_rgba(13,42,27,0.08)] border border-white/40 text-center">
                        <div className="w-12 h-12 rounded-full bg-[#e8f1e4] flex items-center justify-center mx-auto mb-3">
                            <Mail className="w-6 h-6 text-[#2F6B43]" />
                        </div>
                        <div className="font-display text-3xl font-bold tracking-tight">Check your inbox</div>
                        <div className="text-sm text-[#255636] mt-3">
                            We sent a sign-in link to <b>{email}</b>. Tap it and you're in.
                        </div>
                        <div className="text-xs text-[#5b6d63] mt-4">Link works for 20 minutes.</div>
                    </div>
                </motion.div>
            </div>
        );
    }

    return (
        <div className="auth-bg min-h-screen w-full flex items-center justify-center p-5">
            <div className="auth-sun" />
            <div className="auth-heart" />
            <motion.div
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4 }}
                className="relative z-10 w-full max-w-md my-4"
            >
                <div className="flex flex-col items-center text-center mb-6">
                    <img src="/milo-logo.png" alt="Milo" className="h-14 w-auto object-contain mb-6" />
                    <h1 className="font-display text-5xl sm:text-6xl font-bold tracking-tight leading-[1.02]">
                        {presetCode ? "Join them!" : "Hello there"}
                    </h1>
                    <p className="text-base text-[#255636] mt-3">
                        {presetCode ? (
                            <>Invite code <span className="font-black tracking-widest">{presetCode}</span></>
                        ) : (
                            "Let's set up your family."
                        )}
                    </p>
                    {!presetCode && (
                        <p className="text-sm text-[#5b6d63] mt-3">
                            Already have an account?{" "}
                            <Link to="/login" className="font-bold text-[#2F6B43] underline decoration-2 underline-offset-2" data-testid="goto-login-link">
                                Sign in
                            </Link>
                        </p>
                    )}
                </div>

                <div className="bg-white/80 backdrop-blur-xl rounded-[28px] p-6 sm:p-7 shadow-[0_2px_4px_rgba(13,42,27,0.04),0_24px_48px_rgba(13,42,27,0.08)] border border-white/40">
                    <form onSubmit={submit} className="space-y-4">
                        <Field label="Your name" icon={User} placeholder="Alex" value={name} onChange={setName} testId="signup-name-input" required />
                        <Field label="Email" icon={Mail} type="email" placeholder="you@family.com" value={email} onChange={setEmail} testId="signup-email-input" required />
                        {!presetCode && (
                            <div>
                                <div className="mb-2"><FieldLabel icon={Home} label="Household name (optional)" /></div>
                                <input
                                    data-testid="signup-household-input"
                                    className="brut-input"
                                    placeholder="The Chen Family"
                                    value={householdName}
                                    onChange={(e) => setHouseholdName(e.target.value)}
                                />
                            </div>
                        )}
                        <button
                            type="submit"
                            disabled={busy}
                            data-testid="signup-submit-btn"
                            className="w-full flex items-center justify-center gap-2 bg-[#2F6B43] text-white rounded-2xl py-3.5 font-semibold text-base hover:bg-[#255636] transition-colors shadow-[0_6px_20px_rgba(47,107,67,0.3)] disabled:opacity-70 mt-1"
                        >
                            <SparkSolid className="w-4 h-4" />
                            {busy ? "Sending…" : "Send me a sign-in link"}
                        </button>
                        <div className="text-[11px] text-[#5b6d63] text-center">
                            No password needed. We'll email you a one-tap link.
                        </div>
                    </form>
                </div>

                <div className="mt-5 flex items-center justify-center gap-2 text-xs text-[#5b6d63]">
                    <div className="w-8 h-8 rounded-full bg-[#e8f1e4] flex items-center justify-center">
                        <ShieldAlert className="w-4 h-4 text-[#2F6B43]" />
                    </div>
                    <div>Your family's data is safe and private.</div>
                </div>
            </motion.div>
        </div>
    );
}

function FieldLabel({ icon: Icon, label }) {
    return (
        <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded-full bg-[#e8f1e4] flex items-center justify-center">
                <Icon className="w-3.5 h-3.5 text-[#2F6B43]" />
            </div>
            <span className="text-sm font-semibold text-[#0d2a1b]">{label}</span>
        </div>
    );
}

function Field({ label, icon, type = "text", placeholder, value, onChange, testId, required }) {
    return (
        <div>
            <div className="mb-2"><FieldLabel icon={icon} label={label} /></div>
            <input
                data-testid={testId}
                type={type}
                required={required}
                className="brut-input"
                placeholder={placeholder}
                value={value}
                onChange={(e) => onChange(e.target.value)}
            />
        </div>
    );
}
